import React, { useMemo } from 'react';
import { CheckCircle, AlertCircle, XCircle, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

interface SEOAnalyzerProps {
  title: string;
  content: string;
  metaDescription?: string;
  focusKeyword?: string;
  excerpt?: string;
  featuredImageUrl?: string;
  featuredImageAlt?: string;
}

interface SEOCheck {
  id: string;
  label: string;
  status: 'passed' | 'warning' | 'failed';
  message: string;
  score: number;
}

export const SEOAnalyzer: React.FC<SEOAnalyzerProps> = ({
  title,
  content,
  metaDescription,
  focusKeyword,
  excerpt,
  featuredImageUrl,
  featuredImageAlt
}) => {
  const seoChecks = useMemo((): SEOCheck[] => {
    const checks: SEOCheck[] = [];
    const plainTextContent = content.replace(/<[^>]*>/g, '');
    const wordCount = plainTextContent.split(/\s+/).filter(word => word.length > 0).length;
    
    // Extract links for analysis
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const allLinks = doc.querySelectorAll('a[href]');
    let internalLinks = 0;
    let externalLinks = 0;
    
    allLinks.forEach((link) => {
      const href = link.getAttribute('href');
      if (href) {
        if (href.startsWith('/') || href.includes('storymarketing.group')) {
          internalLinks++;
        } else if (href.startsWith('http')) {
          externalLinks++;
        }
      }
    });
    
    // Title checks
    if (title.length === 0) {
      checks.push({
        id: 'title-exists',
        label: 'Title Present',
        status: 'failed',
        message: 'Title is required',
        score: 0
      });
    } else if (title.length < 30) {
      checks.push({
        id: 'title-length',
        label: 'Title Length',
        status: 'warning',
        message: `Title is too short (${title.length} chars). Aim for 30-60 characters.`,
        score: 50
      });
    } else if (title.length > 60) {
      checks.push({
        id: 'title-length',
        label: 'Title Length',
        status: 'warning',
        message: `Title is too long (${title.length} chars). Aim for 30-60 characters.`,
        score: 70
      });
    } else {
      checks.push({
        id: 'title-length',
        label: 'Title Length',
        status: 'passed',
        message: `Title length is optimal (${title.length} characters)`,
        score: 100
      });
    }

    // Meta description checks
    if (!metaDescription) {
      checks.push({
        id: 'meta-description',
        label: 'Meta Description',
        status: 'failed',
        message: 'Meta description is missing',
        score: 0
      });
    } else if (metaDescription.length < 120) {
      checks.push({
        id: 'meta-description',
        label: 'Meta Description',
        status: 'warning',
        message: `Meta description is too short (${metaDescription.length} chars). Aim for 120-160 characters.`,
        score: 60
      });
    } else if (metaDescription.length > 160) {
      checks.push({
        id: 'meta-description',
        label: 'Meta Description',
        status: 'warning',
        message: `Meta description is too long (${metaDescription.length} chars). Aim for 120-160 characters.`,
        score: 70
      });
    } else {
      checks.push({
        id: 'meta-description',
        label: 'Meta Description',
        status: 'passed',
        message: `Meta description length is optimal (${metaDescription.length} characters)`,
        score: 100
      });
    }

    // Focus keyword checks
    if (!focusKeyword) {
      checks.push({
        id: 'focus-keyword',
        label: 'Focus Keyword',
        status: 'warning',
        message: 'No focus keyword set',
        score: 0
      });
    } else {
      const keyword = focusKeyword.toLowerCase();
      const titleContainsKeyword = title.toLowerCase().includes(keyword);
      const contentContainsKeyword = plainTextContent.toLowerCase().includes(keyword);
      const metaContainsKeyword = metaDescription?.toLowerCase().includes(keyword);

      if (titleContainsKeyword && contentContainsKeyword) {
        checks.push({
          id: 'keyword-usage',
          label: 'Keyword Usage',
          status: 'passed',
          message: 'Focus keyword found in title and content',
          score: 100
        });
      } else if (titleContainsKeyword || contentContainsKeyword) {
        checks.push({
          id: 'keyword-usage',
          label: 'Keyword Usage',
          status: 'warning',
          message: 'Focus keyword found in title or content, but not both',
          score: 60
        });
      } else {
        checks.push({
          id: 'keyword-usage',
          label: 'Keyword Usage',
          status: 'failed',
          message: 'Focus keyword not found in title or content',
          score: 20
        });
      }

      // Keyword density
      const keywordCount = (plainTextContent.toLowerCase().match(new RegExp(keyword, 'g')) || []).length;
      const density = (keywordCount / wordCount) * 100;
      
      if (density === 0) {
        checks.push({
          id: 'keyword-density',
          label: 'Keyword Density',
          status: 'failed',
          message: 'Keyword not found in content',
          score: 0
        });
      } else if (density < 0.5) {
        checks.push({
          id: 'keyword-density',
          label: 'Keyword Density',
          status: 'warning',
          message: `Keyword density is low (${density.toFixed(1)}%). Aim for 0.5-2.5%.`,
          score: 50
        });
      } else if (density > 2.5) {
        checks.push({
          id: 'keyword-density',
          label: 'Keyword Density',
          status: 'warning',
          message: `Keyword density is high (${density.toFixed(1)}%). Aim for 0.5-2.5%.`,
          score: 60
        });
      } else {
        checks.push({
          id: 'keyword-density',
          label: 'Keyword Density',
          status: 'passed',
          message: `Keyword density is optimal (${density.toFixed(1)}%)`,
          score: 100
        });
      }
    }

    // Content length checks
    if (wordCount < 300) {
      checks.push({
        id: 'content-length',
        label: 'Content Length',
        status: 'warning',
        message: `Content is short (${wordCount} words). Aim for 300+ words for better SEO.`,
        score: 40
      });
    } else if (wordCount < 600) {
      checks.push({
        id: 'content-length',
        label: 'Content Length',
        status: 'passed',
        message: `Content length is good (${wordCount} words)`,
        score: 80
      });
    } else {
      checks.push({
        id: 'content-length',
        label: 'Content Length',
        status: 'passed',
        message: `Content length is excellent (${wordCount} words)`,
        score: 100
      });
    }

    // Featured image checks
    if (!featuredImageUrl) {
      checks.push({
        id: 'featured-image',
        label: 'Featured Image',
        status: 'warning',
        message: 'No featured image set',
        score: 0
      });
    } else if (!featuredImageAlt) {
      checks.push({
        id: 'image-alt',
        label: 'Image Alt Text',
        status: 'warning',
        message: 'Featured image missing alt text',
        score: 50
      });
    } else {
      checks.push({
        id: 'image-alt',
        label: 'Image Alt Text',
        status: 'passed',
        message: 'Featured image has alt text',
        score: 100
      });
    }

    // Readability checks
    const avgWordsPerSentence = plainTextContent.split(/[.!?]+/).filter(s => s.trim().length > 0).length / wordCount * 100;
    if (avgWordsPerSentence > 20) {
      checks.push({
        id: 'readability',
        label: 'Readability',
        status: 'warning',
        message: 'Consider shorter sentences for better readability',
        score: 60
      });
    } else {
      checks.push({
        id: 'readability',
        label: 'Readability',
        status: 'passed',
        message: 'Content has good readability',
        score: 100
      });
    }

    // Internal links check
    if (internalLinks === 0) {
      checks.push({
        id: 'internal-links',
        label: 'Internal Links',
        status: 'warning',
        message: 'No internal links found. Add links to other articles for better SEO.',
        score: 0
      });
    } else if (internalLinks < 3) {
      checks.push({
        id: 'internal-links',
        label: 'Internal Links',
        status: 'warning',
        message: `Found ${internalLinks} internal link(s). Aim for 3-5 internal links.`,
        score: 50
      });
    } else {
      checks.push({
        id: 'internal-links',
        label: 'Internal Links',
        status: 'passed',
        message: `Good internal linking (${internalLinks} links)`,
        score: 100
      });
    }

    // External links check
    if (externalLinks === 0) {
      checks.push({
        id: 'external-links',
        label: 'External Sources',
        status: 'warning',
        message: 'No external sources cited. Add 2-3 authoritative references.',
        score: 0
      });
    } else if (externalLinks < 2) {
      checks.push({
        id: 'external-links',
        label: 'External Sources',
        status: 'warning',
        message: `Only ${externalLinks} external source(s). Add more authoritative references.`,
        score: 50
      });
    } else {
      checks.push({
        id: 'external-links',
        label: 'External Sources',
        status: 'passed',
        message: `Good source citations (${externalLinks} external links)`,
        score: 100
      });
    }

    // Link ratio check
    const totalLinks = internalLinks + externalLinks;
    if (totalLinks > 0) {
      const internalRatio = (internalLinks / totalLinks) * 100;
      if (internalRatio < 60) {
        checks.push({
          id: 'link-ratio',
          label: 'Link Balance',
          status: 'warning',
          message: `Internal/external link ratio is ${internalRatio.toFixed(0)}/${(100 - internalRatio).toFixed(0)}%. Aim for 70/30.`,
          score: 60
        });
      } else {
        checks.push({
          id: 'link-ratio',
          label: 'Link Balance',
          status: 'passed',
          message: `Good link balance (${internalRatio.toFixed(0)}% internal, ${(100 - internalRatio).toFixed(0)}% external)`,
          score: 100
        });
      }
    }

    return checks;
  }, [title, content, metaDescription, focusKeyword, excerpt, featuredImageUrl, featuredImageAlt]);

  const overallScore = Math.round(
    seoChecks.reduce((sum, check) => sum + check.score, 0) / seoChecks.length
  );

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusIcon = (status: SEOCheck['status']) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
    }
  };

  const getScoreBadge = (score: number) => {
    if (score >= 80) return <Badge className="bg-green-100 text-green-800">Excellent</Badge>;
    if (score >= 60) return <Badge className="bg-yellow-100 text-yellow-800">Good</Badge>;
    if (score >= 40) return <Badge className="bg-orange-100 text-orange-800">Needs Work</Badge>;
    return <Badge className="bg-red-100 text-red-800">Poor</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5" />
          SEO Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Score */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <div className={`text-4xl font-bold ${getScoreColor(overallScore)}`}>
              {overallScore}
            </div>
            <div className="text-sm text-muted-foreground">/ 100</div>
            {getScoreBadge(overallScore)}
          </div>
          <Progress value={overallScore} className="w-full" />
          <p className="text-sm text-muted-foreground">
            Overall SEO Score
          </p>
        </div>

        {/* Detailed Checks */}
        <div className="space-y-3">
          <h4 className="font-semibold">SEO Checklist</h4>
          <div className="space-y-2">
            {seoChecks.map((check) => (
              <div key={check.id} className="flex items-start gap-3 p-3 border rounded-lg">
                {getStatusIcon(check.status)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium">{check.label}</h5>
                    <span className={`text-sm font-medium ${getScoreColor(check.score)}`}>
                      {check.score}%
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {check.message}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
          <div className="text-center">
            <div className="text-2xl font-bold">{title.length}</div>
            <div className="text-xs text-muted-foreground">Title Length</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{metaDescription?.length || 0}</div>
            <div className="text-xs text-muted-foreground">Meta Length</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">
              {content.replace(/<[^>]*>/g, '').split(/\s+/).filter(w => w.length > 0).length}
            </div>
            <div className="text-xs text-muted-foreground">Word Count</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">
              {Math.ceil(content.replace(/<[^>]*>/g, '').split(/\s+/).filter(w => w.length > 0).length / 200)}
            </div>
            <div className="text-xs text-muted-foreground">Read Time (min)</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};